import { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { MButton, MButtonType, MButtonSize, MButtonIconPlacement } from '@marriott/mi-web-ui-kit';
import { useRouter } from 'next/router';
import { useStore } from '../../modules/store/cobrandStore';
import { CtaComponentTypes } from '../../molecules/CtaComponent/CtaComponent.types';
import {
  buildCompareClickTrackValue,
  buildSelectedCardsCompareUrl,
  persistCompareSelections,
  toBoolean,
} from '../../modules/utils';

type ComparisonTrayCompareButtonProps = Pick<
  CtaComponentTypes,
  'openInNewTab' | 'targetExperience' | 'trackingProperties'
> & {
  compareLinkUrl?: string;
  disabled?: boolean;
  compareLinkText?: string;
};

export const ComparisonTrayCompareButton = ({
  compareLinkUrl,
  openInNewTab,
  targetExperience,
  trackingProperties,
  disabled = false,
  compareLinkText,
}: ComparisonTrayCompareButtonProps) => {
  const router = useRouter();
  const { cobrandData } = useStore(store => store);

  const isExternalLink = toBoolean(openInNewTab);
  const linkType = isExternalLink ? 'external' : 'internal';

  const compareButtonUrl = useMemo(
    () =>
      buildSelectedCardsCompareUrl({
        baseUrl: compareLinkUrl ?? '',
        selectedCards: cobrandData.selectedCards,
        routerQuery: router?.query,
        targetExperience,
      }),
    [cobrandData.selectedCards, compareLinkUrl, router?.query, targetExperience]
  );

  const clickTrackValue = useMemo(
    () => buildCompareClickTrackValue({ linkType, trackingProperties }),
    [linkType, trackingProperties]
  );

  const handleCompareClick = useCallback(() => {
    persistCompareSelections({
      cobrandData,
      currentPath: router?.asPath,
    });
  }, [cobrandData, router?.asPath]);

  const isButtonDisabled = disabled || !compareLinkUrl;

  return (
    <MButton
      className={clsx('compare-button mx-auto px-4 text-center', isButtonDisabled && 'disabled')}
      ctaLink={isButtonDisabled ? undefined : compareButtonUrl}
      buttonType={MButtonType.Primary}
      size={MButtonSize.L}
      openInANewTab={isExternalLink}
      disabled={isButtonDisabled}
      trackingProperties={trackingProperties}
      onClick={handleCompareClick}
      data-testid="comparison-tray-compare-button"
      custom_click_track_value={clickTrackValue}
      buttonText={compareLinkText ?? 'Compare'}
      iconPlacement={isExternalLink ? MButtonIconPlacement.Trailing : MButtonIconPlacement.None}
    />
  );
};
